/**
 * AI Configuration Tab
 * 
 * Admin section to configure AI settings for category matching suggestions.
 * Supports OpenAI, Ollama, Anthropic and Mistral providers with Levenshtein fallback.
 * 
 * Redesigned with modern card-based provider selection UI.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Input } from '../../components/ui';
import { aiConfigService } from '../../services';
import type { AIConfig, AIProvider, UpdateAIConfigRequest } from '@budgira/shared';

type TestStatus = 'idle' | 'testing' | 'success' | 'error';

// Provider configuration with cute/stylized icons like Assolyx
const PROVIDERS = [
  {
    id: 'openai' as AIProvider,
    name: 'OpenAI',
    description: 'GPT-4, GPT-4o, GPT-3.5 Turbo',
    color: 'emerald',
    // Cute robot icon (pink/purple)
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
        <rect x="4" y="6" width="16" height="12" rx="2" fill="#E879F9" />
        <rect x="7" y="9" width="3" height="3" rx="1" fill="white" />
        <rect x="14" y="9" width="3" height="3" rx="1" fill="white" />
        <rect x="9" y="14" width="6" height="2" rx="1" fill="white" />
        <rect x="10" y="3" width="4" height="3" rx="1" fill="#E879F9" />
        <circle cx="12" cy="4.5" r="1" fill="white" />
        <rect x="2" y="10" width="2" height="4" rx="1" fill="#E879F9" />
        <rect x="20" y="10" width="2" height="4" rx="1" fill="#E879F9" />
        <rect x="8" y="18" width="3" height="3" rx="1" fill="#E879F9" />
        <rect x="13" y="18" width="3" height="3" rx="1" fill="#E879F9" />
      </svg>
    ),
  },
  {
    id: 'ollama' as AIProvider,
    name: 'Ollama',
    description: 'Serveur local (Llama, Mistral, etc.)',
    color: 'slate',
    // Cute llama icon (pink)
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
        <ellipse cx="12" cy="14" rx="6" ry="7" fill="#F9A8D4" />
        <ellipse cx="12" cy="7" rx="4" ry="4" fill="#F9A8D4" />
        <ellipse cx="10" cy="6.5" rx="1" ry="0.8" fill="#1F2937" />
        <ellipse cx="14" cy="6.5" rx="1" ry="0.8" fill="#1F2937" />
        <ellipse cx="12" cy="8.5" rx="0.8" ry="0.5" fill="#F472B6" />
        <path d="M6 5 L8 8 L7 8 Z" fill="#F9A8D4" />
        <path d="M18 5 L16 8 L17 8 Z" fill="#F9A8D4" />
        <ellipse cx="6.5" cy="5" rx="1" ry="1.5" fill="#F9A8D4" />
        <ellipse cx="17.5" cy="5" rx="1" ry="1.5" fill="#F9A8D4" />
      </svg>
    ),
  },
  {
    id: 'anthropic' as AIProvider,
    name: 'Anthropic',
    description: 'Claude 4, Claude 3.5',
    color: 'orange',
    // Orange sun/circle icon
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
        <circle cx="12" cy="12" r="8" fill="#FB923C" />
        <circle cx="12" cy="12" r="5" fill="#FDBA74" />
        <circle cx="12" cy="12" r="2.5" fill="#FED7AA" />
      </svg>
    ),
  },
  {
    id: 'mistral' as AIProvider,
    name: 'Mistral AI',
    description: 'Mistral Large, Small, Tiny',
    color: 'violet',
    // Purple wind/M icon
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
        <path d="M4 8 Q8 4 12 8 Q16 12 20 8" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        <path d="M4 12 Q8 8 12 12 Q16 16 20 12" stroke="#A78BFA" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        <path d="M4 16 Q8 12 12 16 Q16 20 20 16" stroke="#C4B5FD" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      </svg>
    ),
  },
] as const;

// Color classes for each provider
const colorClasses = {
  emerald: {
    selected: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
    icon: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
  },
  slate: {
    selected: 'border-slate-500 bg-slate-50 dark:bg-slate-800/50',
    icon: 'text-slate-600 dark:text-slate-400',
    iconBg: 'bg-slate-100 dark:bg-slate-800',
  },
  orange: {
    selected: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
    icon: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-orange-100 dark:bg-orange-900/40',
  },
  violet: {
    selected: 'border-violet-500 bg-violet-50 dark:bg-violet-900/20',
    icon: 'text-violet-600 dark:text-violet-400',
    iconBg: 'bg-violet-100 dark:bg-violet-900/40',
  },
};

const AIConfigTab = () => {
  const { t } = useTranslation();
  
  // Form state
  const [config, setConfig] = useState<AIConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Form fields
  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState<AIProvider>('none');
  const [apiKey, setApiKey] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [model, setModel] = useState('');
  const [levenshteinThreshold, setLevenshteinThreshold] = useState(70);
  const [levenshteinEnabled, setLevenshteinEnabled] = useState(true);
  
  // Test connection state
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);
  
  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);
  
  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await aiConfigService.get();
      
      if (response.success && response.data) {
        const data = response.data;
        setConfig(data);
        
        // Set form values
        setEnabled(data.enabled);
        setProvider(data.provider);
        setApiKey(''); // Never show actual key
        setApiEndpoint(data.apiEndpoint || '');
        setModel(data.model || '');
        setLevenshteinThreshold(data.levenshteinThreshold);
        setLevenshteinEnabled(data.levenshteinEnabled);
      }
    } catch (err) {
      setError(t('admin.ai.loadError', 'Erreur lors du chargement de la configuration'));
      console.error('Failed to load AI config:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      const updates: UpdateAIConfigRequest = {
        enabled,
        provider,
        levenshteinThreshold,
        levenshteinEnabled,
      };
      
      // Only include API key if it was changed
      if (apiKey) {
        updates.apiKey = apiKey;
      }
      
      // Include endpoint and model based on provider
      if (provider === 'ollama') {
        updates.apiEndpoint = apiEndpoint || 'http://localhost:11434';
      }
      
      if (provider !== 'none') {
        updates.model = model || undefined;
      }
      
      const response = await aiConfigService.update(updates);
      
      if (response.success) {
        setSuccess(true);
        setApiKey(''); // Clear password field after save
        if (response.data) {
          setConfig(response.data);
        }
      } else {
        setError(response.error?.message || t('admin.ai.saveError', 'Erreur lors de la sauvegarde'));
      }
    } catch (err) {
      setError(t('admin.ai.saveError', 'Erreur lors de la sauvegarde'));
      console.error('Failed to save AI config:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleTestConnection = async () => {
    if (!config) return;
    
    setTestStatus('testing');
    setTestMessage(null);
    
    try {
      const response = await aiConfigService.testConnection({
        provider: config.provider,
        apiKey: config.apiKey,
        apiEndpoint: config.apiEndpoint,
        model: config.model,
      });
      
      if (response.success && response.data) {
        if (response.data.success) {
          setTestStatus('success');
          setTestMessage(response.data.message);
        } else {
          setTestStatus('error');
          setTestMessage(response.data.message);
        }
      } else {
        setTestStatus('error');
        setTestMessage(response.error?.message || t('admin.ai.testFailed', 'Test échoué'));
      }
    } catch (err) {
      setTestStatus('error');
      setTestMessage(t('admin.ai.testFailed', 'Test échoué'));
      console.error('Failed to test AI connection:', err);
    }
  };
  
  // Get default model for each provider
  const getDefaultModel = (providerId: AIProvider): string => {
    switch (providerId) {
      case 'openai': return 'gpt-4o-mini';
      case 'ollama': return 'llama3.2';
      case 'anthropic': return 'claude-3-5-sonnet-20241022';
      case 'mistral': return 'mistral-small-latest';
      default: return '';
    }
  };
  
  // Get model hint text
  const getModelHint = (providerId: AIProvider): string => {
    switch (providerId) {
      case 'openai': return t('admin.ai.openaiModelHint', 'gpt-4o-mini recommandé (économique et performant)');
      case 'ollama': return t('admin.ai.ollamaModelHint', 'llama3.2 ou mistral recommandés');
      case 'anthropic': return t('admin.ai.anthropicModelHint', 'claude-3-5-sonnet recommandé');
      case 'mistral': return t('admin.ai.mistralModelHint', 'mistral-small-latest recommandé');
      default: return '';
    }
  };

  // Get API key hint text
  const getApiKeyHint = (providerId: AIProvider): string => {
    switch (providerId) {
      case 'openai': return t('admin.ai.openaiKeyHint', 'Obtenez votre clé sur platform.openai.com');
      case 'anthropic': return t('admin.ai.anthropicKeyHint', 'Obtenez votre clé sur console.anthropic.com');
      case 'mistral': return t('admin.ai.mistralKeyHint', 'Obtenez votre clé sur console.mistral.ai');
      default: return '';
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  const selectedProviderConfig = PROVIDERS.find(p => p.id === provider);
  
  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
      
      {/* Success message */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-green-700 dark:text-green-400">
            {t('admin.ai.saveSuccess', 'Configuration sauvegardée avec succès')}
          </p>
        </div>
      )}
      
      {/* Header with title and main toggle */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-2.5 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
              <svg className="w-7 h-7 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.ai.title', 'Intelligence Artificielle')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('admin.ai.description', 'Configurez l\'IA pour améliorer votre expérience Budgira')}
              </p>
            </div>
          </div>
          
          {/* Main toggle */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {t('admin.ai.enableAI', 'Activer l\'IA')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('admin.ai.enableAIDesc', 'Suggestions de catégories, import de relevés PDF, et autres fonctionnalités intelligentes.')}
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled(!enabled)}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                enabled ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </Card>
      
      {/* Provider Selection - Only show when enabled */}
      {enabled && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('admin.ai.chooseProvider', 'Choisir un fournisseur')}
            </h3>
            
            {/* Provider Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {PROVIDERS.map((p) => {
                const colors = colorClasses[p.color];
                const isSelected = provider === p.id;
                
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setProvider(p.id);
                      // Set default model when switching providers
                      if (!model || PROVIDERS.find(pr => pr.id !== p.id && getDefaultModel(pr.id) === model)) {
                        setModel(getDefaultModel(p.id));
                      }
                    }}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                      isSelected 
                        ? `${colors.selected} border-2` 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${colors.iconBg} ${colors.icon} flex items-center justify-center mb-3`}>
                      {p.icon}
                    </div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {p.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {p.description}
                    </p>
                    
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <svg className={`w-5 h-5 ${colors.icon}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Provider Configuration */}
            {provider !== 'none' && selectedProviderConfig && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">
                  {t('admin.ai.configOf', 'Configuration de')} {selectedProviderConfig.name}
                </h4>
                
                <div className="space-y-4">
                  {/* API Key - For cloud providers */}
                  {provider !== 'ollama' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('admin.ai.apiKey', 'Clé API')}
                      </label>
                      <div className="relative">
                        <Input
                          type="password"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder={config?.apiKey ? '••••••••••••••••' : 'sk-...'}
                          className="pr-10"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        {getApiKeyHint(provider)}
                      </p>
                    </div>
                  )}
                  
                  {/* Ollama URL */}
                  {provider === 'ollama' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('admin.ai.endpoint', 'URL du serveur Ollama')}
                      </label>
                      <Input
                        type="text"
                        value={apiEndpoint}
                        onChange={(e) => setApiEndpoint(e.target.value)}
                        placeholder="http://localhost:11434"
                      />
                    </div>
                  )}
                  
                  {/* Model Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.ai.model', 'Modèle')}
                    </label>
                    <Input
                      type="text"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      placeholder={getDefaultModel(provider)}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                      {getModelHint(provider)}
                    </p>
                  </div>
                  
                  {/* Test Connection */}
                  <div className="pt-2">
                    <Button
                      variant="secondary"
                      onClick={handleTestConnection}
                      disabled={testStatus === 'testing'}
                    >
                      {testStatus === 'testing' ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                          {t('admin.ai.testing', 'Test en cours...')}
                        </>
                      ) : (
                        t('admin.ai.testConnection', 'Tester la connexion')
                      )}
                    </Button>
                    
                    {testMessage && (
                      <p className={`mt-2 text-sm ${
                        testStatus === 'success' 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {testMessage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
      
      {/* Levenshtein Fallback Configuration */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.ai.levenshteinTitle', 'Correspondance par similarité')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('admin.ai.levenshteinDesc', 'Algorithme de correspondance basé sur la similarité des noms (utilisé si l\'IA n\'est pas disponible)')}
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Enable Levenshtein */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('admin.ai.enableLevenshtein', 'Activer la correspondance par similarité')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('admin.ai.enableLevenshteinDesc', 'Utilisé comme solution de secours si l\'IA n\'est pas disponible')}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={levenshteinEnabled}
                onClick={() => setLevenshteinEnabled(!levenshteinEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  levenshteinEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    levenshteinEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            
            {levenshteinEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.ai.threshold', 'Seuil de correspondance')} ({levenshteinThreshold}%)
                </label>
                <input
                  type="range"
                  min="50"
                  max="100"
                  value={levenshteinThreshold}
                  onChange={(e) => setLevenshteinThreshold(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-600"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>{t('admin.ai.thresholdLoose', 'Souple (50%)')}</span>
                  <span>{t('admin.ai.thresholdStrict', 'Strict (100%)')}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t('admin.ai.thresholdDesc', 'Seules les correspondances avec une similarité supérieure à ce seuil seront suggérées')}
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('common.saving', 'Enregistrement...')}
            </>
          ) : (
            t('common.save', 'Enregistrer')
          )}
        </Button>
      </div>
    </div>
  );
};

export default AIConfigTab;
